/**
 * "Always use Classic cards" — the override that beats any equipped skin (C7).
 *
 * A themed deck is somebody else's choice. The deck and the table belong to
 * the room and come from the host (D87), which is right for the product and
 * means a player can be handed a painted deck they cannot read: the courts are
 * illustrations, the pips are smaller, and at seat size the corner index is
 * all there is. Classic is drawn from text and is legible at any size, so it
 * is what an override falls back *to*.
 *
 * **It is local and it wins.** Nothing the host does, and nothing arriving on
 * the wire, can turn it off — an accessibility setting a room could overrule
 * is not a setting, it is a suggestion. It changes only the faces: the table
 * art and the card back are not a reading problem, and stripping them would
 * turn "I need to read the cards" into "I cannot see what everybody else is
 * playing on".
 *
 * ## Why this has subscribers and `muted` does not
 *
 * Sound is read at the moment something plays, so the component holding the
 * switch is the only thing that needs to know it moved. A skin is read while
 * the table is on screen, and the table has to repaint the instant the switch
 * flips - so this is a store with listeners rather than a getter.
 */

const KEY = 'cards.classicFaces';

const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return window.localStorage.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

/* Read once and kept in memory, because `useSyncExternalStore` compares what
   the snapshot returns and a fresh read per call is a trip to storage on every
   render of every card. */
let current = typeof window === 'undefined' ? false : read();

export function classicFaces(): boolean {
  return current;
}

export function setClassicFaces(value: boolean): void {
  if (value === current) return;
  current = value;
  try {
    window.localStorage.setItem(KEY, value ? 'true' : 'false');
  } catch {
    /* private browsing; the setting simply will not persist */
  }
  for (const listener of listeners) listener();
}

export function subscribeClassicFaces(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
